import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  Provider,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { OPTION_PARENT_COMPONENT, OptionComponent } from '@shared/components/option/option.component';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { BsDropdownDirective } from 'ngx-bootstrap/dropdown';

export const SELECT_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectComponent),
  multi: true
};

export const OPTION_PARENT: Provider = {
  provide: OPTION_PARENT_COMPONENT,
  useExisting: forwardRef(() => MultiSelectComponent)
};

@Component({
  selector: 'gcc-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [OPTION_PARENT, SELECT_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectComponent implements AfterViewInit, OnChanges, ControlValueAccessor {
  @Output() public propagateSelection = new EventEmitter<void>();

  @Input() get value(): any {
    return this._value;
  }
  set value(newValue: any) {
    if (newValue !== this._value) {
      this._value = newValue;
    }
  }

  @ViewChildren(OptionComponent) public renderedOptions: QueryList<OptionComponent>;
  @ViewChild('dropdown', { static: true, read: BsDropdownDirective }) public dropdown: BsDropdownDirective;
  @Input() public placeholder = 'Select';
  @Input() public optionsData: Array<{
    label: string;
    value: string | number;
    iconSrc?: string;
    iconClass?: string;
  }> = [];
  @Input() public isSelectAllAvailable = true;

  @Output() public submitChange = new EventEmitter<number[]>();

  public selectedOptions: number[] = [];
  public bufferedSelectedOptions: string[] = [];
  public disabled: boolean;
  public isOpen: boolean;
  public selectedQuantityLabel = '';

  private _value: any;

  constructor() {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.optionsData && changes.optionsData.currentValue && this.value) {
      this.recalculateSelectedQuantityLabel(this.value.length);
    }
  }

  public toggleSelectAll() {
    if (this.selectedOptions.length !== this.renderedOptions.length) {
      this.selectedOptions = [];

      this.renderedOptions.map(option => {
        option.select();
        this.selectedOptions.push(option.value);
      });

      this.selectedOptions = [...this.selectedOptions];
      this.selectedQuantityLabel = 'All';
    } else {
      this.renderedOptions.map(option => option.deselect());
      this.selectedOptions = [];
      this.selectedQuantityLabel = this.placeholder;
    }
  }

  public writeValue(value: any): void {
    this.value = value;

    if (this.optionsData && this.value) {
      this.recalculateSelectedQuantityLabel(this.value.length);
    }
  }

  public onCancel(): void {
    this.bufferedSelectedOptions.forEach(item => {
      this.renderedOptions.find(select => select.value === item).select();
    });

    this.recalculateSelectedQuantityLabel(this.value.length);
    this.isOpen = false;
    this.dropdown.hide();
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public handleDropdownToggle(isOpen: boolean) {
    this.isOpen = isOpen;
    if (isOpen) {
      this.bufferedSelectedOptions = this.value || [];
      this._onTouched();
      setTimeout(() => {
        this._setSelectionByValue(this.value);
      });
      this.selectedOptions = [...this.selectedOptions];
      return;
    } else {
      this.onCancel();
    }
  }

  public toggle(opt: OptionComponent): void {
    if (opt.selected) {
      this.selectedOptions = this.selectedOptions.filter(item => item !== opt.value);
    } else {
      this.selectedOptions.push(opt.value);
    }
    this.selectedOptions = [...this.selectedOptions];
    opt.selected ? opt.deselect() : opt.select();
    this.recalculateSelectedQuantityLabel(this.renderedOptions.filter(item => item.selected).length);
  }

  public onApply(): void {
    this.propagateChanges();
    this.dropdown.hide();
  }

  public propagateChanges(): void {
    let valueToEmit: any = null;
    valueToEmit = this.selectedOptions;
    this._value = valueToEmit;
    this.submitChange.emit(valueToEmit);
    this._onChange(valueToEmit);
  }

  public ngAfterViewInit(): void {
    if (this.value) {
      // For avoiding error ExpressionChangedAfterItHasBeenCheckedError postpone operation to microtask  after form initialization
      Promise.resolve().then(() => {
        this._setSelectionByValue(this._value);
      });
    }
  }

  public compareArrays(array1, array2) {
    if (array1.length !== array2.length || (array1.length === 0 && array2.length === 0)) {
      return false;
    }

    array1 = array1.slice();
    array1.sort();
    array2 = array2.slice();
    array2.sort();

    for (let i = 0; i < array1.length; i++) {
      if (array1[i] !== array2[i]) {
        return false;
      }
    }

    return true;
  }

  private recalculateSelectedQuantityLabel(quantity: number): void {
    const selectedItemsLength = quantity;

    if (selectedItemsLength === 0) {
      this.selectedQuantityLabel = this.placeholder;
    } else if (selectedItemsLength !== this.optionsData.length) {
      this.selectedQuantityLabel = `${selectedItemsLength} selected`;
    } else {
      this.selectedQuantityLabel = 'All';
    }
  }

  private _setSelectionByValue(value: any | any[]): void {
    this.renderedOptions.forEach(opt => opt.deselect());
    this.selectedOptions = [];

    if (value) {
      if (!Array.isArray(value)) {
        throw getSelectNonArrayValueError();
      }
      value.forEach((currentValue: any) => this._selectValue(currentValue));
    }
  }

  private _selectValue(value: any): void {
    const correspondingOption = this.renderedOptions.find((option: OptionComponent) => {
      return option.value === value;
    });

    if (correspondingOption) {
      correspondingOption.select();
      this.selectedOptions.push(value);
    }
  }

  private _onChange: (value: any) => void = () => {};
  private _onTouched = () => {};
}

export function getSelectNonArrayValueError(): Error {
  return Error('Value must be an array');
}
